import React, { useState, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "../components/Input";
import Label from "../components/Label";
import { useReduxDispatch, useReduxSelector } from "../redux/hooks";
import { UserAPI } from "../middleware";
import { Alert } from "@mui/material";
import { LoadingModal } from "../components/Modal";
import {
  SignupFailed,
  SignupPending,
  SignupSuccessful,
} from "../redux/slice/SignupSlice";
import { BsChatLeftDotsFill } from "react-icons/bs";

type Inputs = {
  password: string;
  confirmPassword: string;
};

type ErrorItem = {
  message: string;
  field?: string;
};

const SetNewPasswordScreen = () => {
  const { isSignedIn } = useReduxSelector((state) => state.user);
  const { isLoading } = useReduxSelector((state) => state.signup);

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<ErrorItem[]>([]);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [userData, setUserData] = useState<any>(null);
  const [dataSource, setDataSource] = useState<string>("none");

  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const location = useLocation();
  const { state } = location;

  const handleClose = () => setOpen(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  // Load user data on component mount
  useEffect(() => {
    const loadUserData = () => {
      try {
        console.log("Loading user data for password reset, location state:", state);

        // First attempt: Try to get data from navigation state
        if (state && state.signUpResponseData && state.otp && state.user) {
          console.log("Using navigation state data for password reset");
          setUserData({
            signUpResponseData: state.signUpResponseData,
            otp: state.otp,
            user: state.user,
            patientContact: state.patientContact
          });
          setDataSource("navigation");
          return true;
        }

        // Second attempt: Try to get data from localStorage
        const savedData = localStorage.getItem('verifiedSignupData');
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            console.log("Using localStorage data for password reset:", parsedData);

            if (parsedData.signUpResponseData && parsedData.otp && parsedData.user) {
              setUserData(parsedData);
              setDataSource("localStorage");
              return true;
            }
          } catch (e) {
            console.error("Error parsing localStorage data:", e);
          }
        }

        // No valid data found
        console.error("No valid user data found for password reset");
        setError([{ message: "Missing signup data. Please try the signup process again." }]);

        // Redirect back to signup after a delay
        setTimeout(() => {
          navigate("/signup", { replace: true });
        }, 5000);

        return false;
      } catch (error: any) {
        console.error("Error loading user data:", error);
        setError([{ message: error.message || "Failed to load user data for password reset" }]);
        return false;
      }
    };

    loadUserData();
  }, [state, navigate]);

  const onSubmit: SubmitHandler<Inputs> = async () => {
    if (!userData || !userData.signUpResponseData || !userData.otp || !userData.user) {
      setError([{ message: "Missing data required for password reset" }]);
      return;
    }

    // Password validation
    if (password !== confirmPassword) {
      setError([{ message: "Passwords do not match" }]);
      return;
    }

    if (password.length < 8) {
      setError([{ message: "Password must be at least 8 characters long" }]);
      return;
    }

    setOpen(true);
    dispatch(SignupPending());

    try {
      console.log("Resetting password with data from source:", dataSource);

      // Validate user data is available
      if (!userData.user || !userData.user.id || !userData.user.username) {
        throw new Error('Missing user data required for password reset');
      }

      const response = await UserAPI.resetPasswordAfterOTP(
        userData.signUpResponseData,
        userData.otp,
        userData.user,
        password,
        confirmPassword
      );
      console.log("Password reset response:", response);

      if (response.status === 200) {
        // Clear localStorage data as it's no longer needed
        localStorage.removeItem('verifiedSignupData');
        localStorage.removeItem('signUpData');

        setOpen(false);
        navigate("/signin", {
          state: {
            signUpResponseData: userData.signUpResponseData,
            passwordReset: true,
            username: userData.user.username,
          }
        });
      } else {
        throw new Error('Failed to reset password');
      }
    } catch (error: any) {
      const errorMessage: ErrorItem[] = error?.response?.data?.errors || [{ message: error.message || "Unknown error" }];
      dispatch(SignupFailed(errorMessage));
      setOpen(false);
      setError(errorMessage);
    }
  };

  return (
    <>
      {!isSignedIn ? (
        <div className="py-8 md:py-0 px-8 md:px-14 h-screen grid grid-cols-1 md:grid-cols-2 gap-6 items-center relative overflow-x-hidden">
          <div className="flex flex-col justify-center z-10">
            <div className="flex justify-center items-center">
              <img
                src="/assets/trust-hospital-logo.png"
                alt="hospital logo"
                className="w-72 cursor-pointer"
                onClick={() => navigate("/")}
              />
            </div>
            <div className="px-6 md:px-10 border shadow-sm border-tertiary-200 rounded-xl flex flex-col justify-center py-6 mt-6 max-w-xl">
              <h1 className="text-4xl font-medium text-center">Set New Password</h1>
              {error.length > 0 &&
                error.map((item, index) => (
                  <div key={index}>
                    <Alert severity="error" className="mt-2">
                      {item.message}
                    </Alert>
                  </div>
                ))}

              {userData ? (
                <div className="mt-8 w-full flex flex-col">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    {...register("password", { required: true })}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="h-10 px-2 shadow-sm focus:ring-primary-100 focus:border-primary-100 focus:outline-primary-100 block w-full sm:text-sm border border-tertiary-200 rounded-[11px] bg-white"
                  />
                  {errors.password && <p className="text-red-500">Password is required</p>}

                  <label htmlFor="confirmPassword" className="mt-4">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    {...register("confirmPassword", { required: true })}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="h-10 px-2 shadow-sm focus:ring-primary-100 focus:border-primary-100 focus:outline-primary-100 block w-full sm:text-sm border border-tertiary-200 rounded-[11px] bg-white"
                  />
                  {errors.confirmPassword && <p className="text-red-500">Confirm password is required</p>}

                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-red-500 mt-2">Passwords do not match</p>
                  )}

                  <button
                    className={`w-full mt-6 h-10 rounded-2xl font-medium text-md ${password && confirmPassword && password === confirmPassword && password.length >= 8
                      ? 'bg-primary-50 text-white hover:opacity-90'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    onClick={handleSubmit(onSubmit)}
                    disabled={!password || !confirmPassword || password !== confirmPassword || password.length < 8}
                  >
                    Continue
                  </button>

                  {dataSource !== "none" && (
                    <div className="mt-2 text-xs text-gray-500 text-center">
                      Using data from: {dataSource}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-8 text-center">
                  <p className="text-lg">Loading user data...</p>
                  <p className="text-sm text-gray-500 mt-2">
                    If this takes too long, you'll be redirected to the signup page.
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="h-full w-full ">
            <img
              src={"/assets/manwithbluebg.png"}
              alt="sign up illustration"
              className="w-full h-full object-contain absolute left-[400px] 2xl:left-[500px] 3xl:left-[550px]"
            />
          </div>
          <LoadingModal
            open={open}
            handleClose={handleClose}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default SetNewPasswordScreen;
